// frontend/src/hooks/useInvalidateMapOnOrientation.js
import { useEffect, useRef } from "react";

const DEFAULT_PASSES_MS = [150, 400, 800];

/**
 * Run Leaflet invalidateSize after layout has a chance to settle.
 */
function runInvalidate(mapRef) {
  try {
    const map = mapRef?.current;
    if (!map) return;
    const container = map.getContainer?.();
    if (container) void container.offsetHeight;
    map.invalidateSize(true);
  } catch {
    // map may unmount during rotation
  }
}

/**
 * Fixes Leaflet rendering on orientation / viewport changes (mobile PWA).
 * - Multi-pass invalidate on resize / orientation (catches late safe-area + dvh).
 * - visualViewport (throttled): standalone PWA often gets this when window.resize lags.
 */
export function useInvalidateMapOnOrientation(mapRef, options = {}) {
  const passes = Array.isArray(options.passes) && options.passes.length > 0
    ? options.passes
    : DEFAULT_PASSES_MS;

  const passesKey = passes.join(",");
  const timeoutsRef = useRef([]);

  useEffect(() => {
    if (!mapRef) return;

    const clearPasses = () => {
      timeoutsRef.current.forEach((id) => clearTimeout(id));
      timeoutsRef.current = [];
    };

    const scheduleMultiPass = () => {
      clearPasses();
      passes.forEach((ms) => {
        const id = setTimeout(() => runInvalidate(mapRef), ms);
        timeoutsRef.current.push(id);
      });
    };

    const onResizeOrOrientation = () => scheduleMultiPass();

    let vvLast = 0;
    const VV_THROTTLE_MS = 100;
    const onVisualViewportResize = () => {
      const now = Date.now();
      if (now - vvLast < VV_THROTTLE_MS) return;
      vvLast = now;
      runInvalidate(mapRef);
    };

    window.addEventListener("resize", onResizeOrOrientation);
    window.addEventListener("orientationchange", onResizeOrOrientation);

    const mq = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(orientation: landscape)")
      : null;
    if (mq?.addEventListener) {
      mq.addEventListener("change", onResizeOrOrientation);
    }

    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (vv) {
      vv.addEventListener("resize", onVisualViewportResize);
    }

    return () => {
      clearPasses();
      window.removeEventListener("resize", onResizeOrOrientation);
      window.removeEventListener("orientationchange", onResizeOrOrientation);
      if (mq?.removeEventListener) {
        mq.removeEventListener("change", onResizeOrOrientation);
      }
      if (vv) {
        vv.removeEventListener("resize", onVisualViewportResize);
      }
    };
  }, [mapRef, passesKey]);
}
